import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiBarChart, FiTrendingUp, FiShoppingCart, FiUsers, FiDownload, FiArrowRight, FiDatabase, FiCheckCircle, FiMail } from 'react-icons/fi';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

export const IndustryReportsPage: React.FC = () => {
  useDocumentTitle(
    'Digital Marketing Industry Reports & Benchmarks | Mirani Marketing',
    'Access data-driven digital marketing reports and industry benchmarks. Learn average CPC, conversion rates, seasonal trends, and budget allocation strategies.'
  );

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('http://localhost:5000/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          message: 'Request for Industry Reports download',
          type: 'industry-reports'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const reports = [
    {
      icon: FiShoppingCart,
      title: 'E-commerce Performance Report',
      shortDesc: 'Comprehensive analysis of e-commerce conversion metrics, cart behavior, and revenue optimization strategies.',
      fullDesc: [
        'This report provides in-depth insights into e-commerce performance across multiple product categories including fashion, electronics, home goods, and beauty. We analyze conversion rates from first visit to final purchase, identifying drop-off points in the customer journey.',
        'Cart abandonment remains one of the biggest challenges for online retailers. Our data shows that the average cart abandonment rate sits at 68%, with significant variations by industry. We break down the reasons behind abandonment and provide actionable recovery strategies.',
        'The report also covers Average Order Value (AOV) benchmarks segmented by traffic source, device type, and customer acquisition channel. Understanding these metrics helps businesses optimize pricing, upsell strategies, and promotional campaigns for maximum revenue per transaction.'
      ],
      metrics: ['2.8% avg conversion rate', '68% cart abandonment', '₹2,400 average order value', 'Mobile vs Desktop performance', 'Seasonal purchase patterns']
    },
    {
      icon: FiUsers,
      title: 'Lead Generation Benchmarks',
      shortDesc: 'Industry-specific cost per lead data and conversion benchmarks across B2B and B2C sectors.',
      fullDesc: [
        'Lead generation costs vary dramatically by industry, target audience, and channel mix. This report analyzes cost per lead (CPL) across 12+ industries including B2B SaaS, real estate, education, healthcare, financial services, and professional consulting. Each sector has unique challenges and opportunities.',
        'Form completion rates are critical to understanding lead quality. Our data reveals that the average form completion rate is 3.2%, but this can range from 1.8% for complex forms to 6.5% for optimized single-step captures. We provide recommendations for form design, field reduction, and progressive profiling.',
        'The lead-to-customer conversion rate averages 45% across industries, but varies significantly based on lead nurturing strategies, sales process efficiency, and product-market fit. We break down the entire funnel from impression to closed deal, helping you identify bottlenecks and optimization opportunities.'
      ],
      metrics: ['₹420 avg CPL (B2B)', '3.2% avg form completion', '45% lead-to-customer rate', 'Industry-specific benchmarks', 'Channel performance comparison']
    },
    {
      icon: FiTrendingUp,
      title: 'CPC & CPM Trends',
      shortDesc: 'Real-time cost metrics for Google Ads, Meta Ads, and LinkedIn Ads across 15+ industries.',
      fullDesc: [
        'Advertising costs fluctuate based on seasonality, competition, and platform algorithm changes. This report tracks Cost Per Click (CPC) and Cost Per Thousand Impressions (CPM) trends across Google Search, Google Display Network, Facebook, Instagram, and LinkedIn. Data is updated monthly to reflect current market conditions.',
        'Average CPC for Google Search Ads sits at ₹18 across all industries, but ranges from ₹8 for low-competition keywords to ₹120+ for highly competitive terms in finance and legal sectors. We provide industry-specific breakdowns and keyword category analysis to help you budget accurately.',
        'Year-over-year increases averaged 12% in 2025, driven by increased advertiser competition and platform saturation. However, some industries saw decreases due to improved targeting capabilities and audience segmentation. Understanding these trends helps you forecast budgets and adjust bidding strategies proactively.'
      ],
      metrics: ['₹18 avg CPC (search)', '₹220 avg CPM (display)', '+12% YoY increase', 'Platform-wise breakdown', 'Peak vs off-peak costs']
    },
    {
      icon: FiBarChart,
      title: 'Social Media Engagement Trends',
      shortDesc: 'Engagement rates, optimal posting times, and content performance benchmarks for major social platforms.',
      fullDesc: [
        'Social media engagement rates have stabilized at 4.2% across platforms, but vary significantly by content type, industry, and audience size. Video content consistently outperforms static images by 65%, while carousel posts drive 48% more clicks than single-image posts. Understanding these patterns is crucial for content strategy.',
        'Timing matters more than ever. Our analysis of over 500,000 posts reveals that 7-9 PM IST is the optimal posting window for Indian audiences across Facebook, Instagram, and LinkedIn. However, B2B content performs better during work hours (10 AM - 2 PM), while B2C peaks in evening hours.',
        'Platform-specific insights show Instagram Stories have 2x higher completion rates than feed posts, LinkedIn articles generate 5x more leads than text posts, and YouTube Shorts are driving 3x more subscribers than traditional long-form content. This report provides platform-by-platform strategies tailored to your business goals.'
      ],
      metrics: ['4.2% avg engagement rate', 'Best time: 7-9 PM IST', 'Video content +65% engagement', 'Platform-specific insights', 'Influencer vs organic performance']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20}}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <FiBarChart className="text-6xl text-white mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Digital Marketing Industry Reports & Benchmarks
            </h1>
            <p className="text-lg text-white/90">
              Data-driven insights to inform your marketing strategy
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Data Matters */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Why Data Matters</h2>
            <p className="text-lg text-gray-600">
              Businesses relying on assumptions lose budget. Data-driven decisions increase profitability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Reports Categories */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Reports Categories</h2>
          </motion.div>

          <div className="space-y-12">
            {reports.map((report, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 md:p-12 shadow-xl hover:shadow-2xl transition-all border border-gray-100"
              >
                <div className="flex items-start gap-6 mb-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center">
                      <report.icon className="text-3xl text-purple-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">{report.title}</h3>
                    <p className="text-lg text-gray-600 font-semibold">{report.shortDesc}</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  {report.fullDesc.map((paragraph, idx) => (
                    <p key={idx} className="text-gray-600 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6">
                  <h4 className="font-bold text-gray-800 mb-4">Key Metrics Included:</h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    {report.metrics.map((metric, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <FiCheckCircle className="text-purple-600 flex-shrink-0" />
                        <span className="text-gray-700">{metric}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">What You'll Learn From These Reports</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Average CPC by industry',
                'Conversion benchmarks',
                'Seasonal trends',
                'Budget allocation strategies'
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 shadow-md flex items-center gap-4"
                >
                  <FiArrowRight className="text-purple-600 text-2xl" />
                  <span className="font-semibold text-gray-700">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Data Sources Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-12">
              <FiDatabase className="text-5xl text-purple-600 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Data Sources</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Our reports are built on aggregated campaign data and comprehensive industry insights
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-8 text-center"
              >
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <span className="text-2xl font-bold text-purple-600">250+</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Active Campaigns</h3>
                <p className="text-gray-600">
                  Data aggregated from over 250 active campaigns across multiple industries and platforms
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-8 text-center"
              >
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <span className="text-2xl font-bold text-purple-600">₹50M+</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Ad Spend Analyzed</h3>
                <p className="text-gray-600">
                  Over ₹50 million in advertising spend analyzed monthly across Google, Meta, and LinkedIn
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-8 text-center"
              >
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <span className="text-2xl font-bold text-purple-600">15+</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Industries Covered</h3>
                <p className="text-gray-600">
                  Comprehensive benchmarks spanning e-commerce, B2B SaaS, education, healthcare, and more
                </p>
              </motion.div>
            </div>

            <div className="mt-12 bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">Our Methodology</h3>
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="text-purple-600 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">
                    <strong>Real Campaign Data:</strong> All metrics derived from actual running campaigns, not hypothetical scenarios
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="text-purple-600 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">
                    <strong>Privacy Protected:</strong> All data is anonymized and aggregated to protect client confidentiality
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="text-purple-600 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">
                    <strong>Monthly Updates:</strong> Reports refreshed monthly to reflect current market conditions and trends
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="text-purple-600 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">
                    <strong>Expert Analysis:</strong> Data interpreted by experienced digital marketing strategists with 10+ years in the industry
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Download Section - Email Gate Form */}
      <section className="py-16 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        
        <div className="container mx-auto px-4 max-w-3xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <FiDownload className="text-5xl text-white mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Download All Reports</h2>
              <p className="text-lg text-white/90">
                Get instant access to comprehensive industry data and benchmarks. Enter your details below to receive the complete report package.
              </p>
            </div>

            {submitStatus === 'success' ? (
              <div className="bg-white rounded-2xl p-8 text-center">
                <FiCheckCircle className="text-6xl text-green-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h3>
                <p className="text-gray-600 mb-4">
                  We've received your request. Check your email for the download links to all industry reports.
                </p>
                <p className="text-sm text-gray-500">
                  If you don't see the email within a few minutes, please check your spam folder.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 md:p-10 shadow-2xl">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="your.email@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Company Name (Optional)
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="Your company name"
                    />
                  </div>

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                      Failed to submit. Please try again or contact us directly.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 transition-all flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <FiMail />
                        Download All Reports
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    By downloading, you agree to receive marketing communications from Mirani Marketing. You can unsubscribe at any time.
                  </p>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default IndustryReportsPage;
